import { useState, useEffect } from 'react';
import api, { formatINR, SQM_TO_SQFT } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Calculator, ArrowLeftRight, BarChart3 } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';

export default function RateCalculatorPage() {
  const [vendors, setVendors] = useState([]);
  const [designTypes, setDesignTypes] = useState([]);
  const [form, setForm] = useState({ vendor_id: '', design_type: '', width_mm: '', height_mm: '' });
  const [result, setResult] = useState(null);
  const [compareForm, setCompareForm] = useState({ vendor1: '', vendor2: '', design_type: '', width_mm: '', height_mm: '' });
  const [compareResult, setCompareResult] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get('/vendors').then(r => setVendors(r.data)).catch(() => {});
    api.get('/design-types').then(r => setDesignTypes(r.data)).catch(() => {});
  }, []);

  const calculate = async () => {
    if (!form.vendor_id || !form.design_type || !form.width_mm || !form.height_mm) return;
    setLoading(true);
    try {
      const { data } = await api.post('/calculator/estimate', {
        vendor_id: form.vendor_id,
        design_type: form.design_type,
        width_mm: parseFloat(form.width_mm),
        height_mm: parseFloat(form.height_mm),
      });
      setResult(data);
    } catch (e) {
      setResult({ error: e.response?.data?.detail || 'No matching products found' });
    }
    setLoading(false);
  };

  const compare = async () => {
    if (!compareForm.vendor1 || !compareForm.vendor2 || !compareForm.design_type || !compareForm.width_mm || !compareForm.height_mm) return;
    setLoading(true);
    try {
      const { data } = await api.post('/calculator/compare', {
        vendor_ids: [compareForm.vendor1, compareForm.vendor2],
        design_type: compareForm.design_type,
        width_mm: parseFloat(compareForm.width_mm),
        height_mm: parseFloat(compareForm.height_mm),
      });
      setCompareResult(data);
    } catch {
      setCompareResult(null);
    }
    setLoading(false);
  };

  const liveArea = (w, h) => {
    const wm = parseFloat(w) / 1000 || 0;
    const hm = parseFloat(h) / 1000 || 0;
    const sqm = wm * hm;
    return { sqm: sqm.toFixed(3), sqft: (sqm * SQM_TO_SQFT).toFixed(2) };
  };

  return (
    <div className="space-y-6" data-testid="calculator-page">
      <div>
        <h1 className="font-['Outfit'] text-3xl font-semibold tracking-tight text-slate-900">Rate Calculator</h1>
        <p className="text-slate-500 text-sm mt-1">Estimate rates and compare vendors</p>
      </div>

      <Tabs defaultValue="estimate" className="w-full">
        <TabsList className="bg-slate-100 p-1">
          <TabsTrigger value="estimate" className="data-[state=active]:bg-white" data-testid="tab-estimate">
            <Calculator size={14} className="mr-1.5" /> Estimate
          </TabsTrigger>
          <TabsTrigger value="compare" className="data-[state=active]:bg-white" data-testid="tab-compare">
            <ArrowLeftRight size={14} className="mr-1.5" /> Compare
          </TabsTrigger>
        </TabsList>

        {/* Estimate Tab */}
        <TabsContent value="estimate" className="mt-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="border border-slate-200">
              <CardHeader className="pb-3"><CardTitle className="text-lg font-['Outfit']">Enter Details</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Vendor</Label>
                  <Select value={form.vendor_id} onValueChange={v => setForm({...form, vendor_id: v})}>
                    <SelectTrigger data-testid="calc-vendor-select"><SelectValue placeholder="Select vendor" /></SelectTrigger>
                    <SelectContent>
                      {vendors.map(v => <SelectItem key={v.id} value={v.id}>{v.company_name} ({v.material_type})</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Design Type</Label>
                  <Select value={form.design_type} onValueChange={v => setForm({...form, design_type: v})}>
                    <SelectTrigger data-testid="calc-design-select"><SelectValue placeholder="Select design" /></SelectTrigger>
                    <SelectContent>
                      {designTypes.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Width (mm)</Label><Input type="number" value={form.width_mm} onChange={e => setForm({...form, width_mm: e.target.value})} placeholder="e.g., 1200" data-testid="calc-width-input" /></div>
                  <div><Label>Height (mm)</Label><Input type="number" value={form.height_mm} onChange={e => setForm({...form, height_mm: e.target.value})} placeholder="e.g., 1500" data-testid="calc-height-input" /></div>
                </div>
                {form.width_mm && form.height_mm && (
                  <div className="bg-slate-50 rounded-lg p-3 text-sm">
                    <span className="text-slate-500">Area: </span>
                    <span className="font-mono font-medium">{liveArea(form.width_mm, form.height_mm).sqm} Sqm</span>
                    <span className="text-slate-400 mx-1">=</span>
                    <span className="font-mono font-medium">{liveArea(form.width_mm, form.height_mm).sqft} Sq.ft</span>
                  </div>
                )}
                <Button onClick={calculate} className="w-full bg-[#ed8936] hover:bg-[#dd6b20] text-white" disabled={loading} data-testid="calculate-btn">
                  {loading ? 'Calculating...' : 'Calculate Rate'}
                </Button>
              </CardContent>
            </Card>

            {/* Result */}
            <Card className={`border ${result ? 'border-[#ed8936]/30' : 'border-slate-200'}`}>
              <CardHeader className="pb-3"><CardTitle className="text-lg font-['Outfit']">Estimation Result</CardTitle></CardHeader>
              <CardContent>
                {result?.error ? (
                  <div className="text-red-500 text-sm bg-red-50 p-4 rounded-lg">{result.error}</div>
                ) : result ? (
                  <div className="space-y-4">
                    <div className="bg-[#1a365d] text-white rounded-lg p-4">
                      <p className="text-xs uppercase tracking-wider text-white/60 mb-1">Estimated Unit Price</p>
                      <p className="text-3xl font-['Outfit'] font-semibold" data-testid="estimated-price">{formatINR(result.estimated_unit_price)}</p>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="bg-slate-50 rounded-lg p-3">
                        <p className="text-xs text-slate-400 mb-1">Rate per Sqm</p>
                        <p className="font-mono font-medium text-[#1a365d]" data-testid="rate-per-sqm">{formatINR(result.rate_per_sqm)}</p>
                      </div>
                      <div className="bg-slate-50 rounded-lg p-3">
                        <p className="text-xs text-slate-400 mb-1">Rate per Sq.ft</p>
                        <p className="font-mono font-medium text-[#1a365d]" data-testid="rate-per-sqft">{formatINR(result.rate_per_sqft)}</p>
                      </div>
                      <div className="bg-slate-50 rounded-lg p-3">
                        <p className="text-xs text-slate-400 mb-1">Area</p>
                        <p className="font-mono text-sm">{result.area_sqm} Sqm ({result.area_sqft} Sq.ft)</p>
                      </div>
                      <div className="bg-slate-50 rounded-lg p-3">
                        <p className="text-xs text-slate-400 mb-1">Glass</p>
                        <p className="text-sm">{result.glass_type || 'Standard'}</p>
                      </div>
                    </div>
                    <div className="text-xs text-slate-400 mt-2">
                      Vendor: {result.vendor_name} | Material: {result.material_type}
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center h-48 text-slate-400">
                    <Calculator size={40} className="mb-2 opacity-30" />
                    <p className="text-sm">Select vendor, design and enter dimensions to estimate</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Compare Tab */}
        <TabsContent value="compare" className="mt-4">
          <Card className="border border-slate-200">
            <CardContent className="p-5">
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-4">
                <div>
                  <Label className="text-xs">Vendor 1</Label>
                  <Select value={compareForm.vendor1} onValueChange={v => setCompareForm({...compareForm, vendor1: v})}>
                    <SelectTrigger data-testid="compare-vendor1-select"><SelectValue placeholder="Vendor 1" /></SelectTrigger>
                    <SelectContent>{vendors.map(v => <SelectItem key={v.id} value={v.id}>{v.company_name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Vendor 2</Label>
                  <Select value={compareForm.vendor2} onValueChange={v => setCompareForm({...compareForm, vendor2: v})}>
                    <SelectTrigger data-testid="compare-vendor2-select"><SelectValue placeholder="Vendor 2" /></SelectTrigger>
                    <SelectContent>{vendors.map(v => <SelectItem key={v.id} value={v.id}>{v.company_name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Design Type</Label>
                  <Select value={compareForm.design_type} onValueChange={v => setCompareForm({...compareForm, design_type: v})}>
                    <SelectTrigger><SelectValue placeholder="Design" /></SelectTrigger>
                    <SelectContent>{designTypes.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Width (mm)</Label>
                  <Input type="number" value={compareForm.width_mm} onChange={e => setCompareForm({...compareForm, width_mm: e.target.value})} />
                </div>
                <div>
                  <Label className="text-xs">Height (mm)</Label>
                  <Input type="number" value={compareForm.height_mm} onChange={e => setCompareForm({...compareForm, height_mm: e.target.value})} />
                </div>
              </div>
              <Button onClick={compare} className="bg-[#1a365d] hover:bg-[#102a4c] text-white" disabled={loading} data-testid="compare-btn">
                <ArrowLeftRight size={14} className="mr-1.5" /> Compare Rates
              </Button>
            </CardContent>
          </Card>

          {compareResult && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
              {compareResult.results.map((r, i) => (
                <Card key={i} className={`border-2 ${i === 0 ? 'border-[#ed8936]' : 'border-[#1a365d]'}`} data-testid={`compare-result-${i}`}>
                  <CardContent className="p-5">
                    <div className={`text-xs font-bold uppercase tracking-wider ${i === 0 ? 'text-[#ed8936]' : 'text-[#1a365d]'} mb-1`}>Vendor {i + 1}</div>
                    <h3 className="font-['Outfit'] font-semibold text-lg">{r.vendor_name}</h3>
                    <p className="text-xs text-slate-400 mb-3">{r.material_type}</p>
                    {r.error ? (
                      <p className="text-red-500 text-sm">{r.error}</p>
                    ) : (
                      <div className="space-y-2">
                        <div className="bg-slate-50 rounded-lg p-3">
                          <p className="text-xs text-slate-400">Estimated Price</p>
                          <p className="text-2xl font-['Outfit'] font-semibold text-[#1a365d]">{formatINR(r.estimated_price)}</p>
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-sm">
                          <div><span className="text-slate-400">Rate/Sqm:</span> <span className="font-mono">{formatINR(r.rate_per_sqm)}</span></div>
                          <div><span className="text-slate-400">Rate/Sqft:</span> <span className="font-mono">{formatINR(r.rate_per_sqft)}</span></div>
                          <div><span className="text-slate-400">Glass:</span> {r.glass_type}</div>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}

              {/* Comparison Chart */}
              {compareResult.results.every(r => !r.error) && (
                <Card className="lg:col-span-2 border border-slate-200">
                  <CardContent className="p-5">
                    <h4 className="font-['Outfit'] font-medium mb-3 flex items-center gap-2"><BarChart3 size={16} className="text-[#ed8936]" /> Rate Comparison</h4>
                    <ResponsiveContainer width="100%" height={200}>
                      <BarChart data={compareResult.results.map(r => ({ name: r.vendor_name, price: r.estimated_price, rate: r.rate_per_sqm }))}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                        <YAxis tick={{ fontSize: 12 }} />
                        <Tooltip formatter={(v) => formatINR(v)} />
                        <Bar dataKey="price" radius={[4, 4, 0, 0]}>
                          {compareResult.results.map((_, i) => (
                            <Cell key={i} fill={i === 0 ? '#ed8936' : '#1a365d'} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                    <p className="text-center text-xs text-slate-400 mt-2">
                      Area: {compareResult.area_sqm} Sqm ({compareResult.area_sqft} Sq.ft) | Design: {compareResult.design_type}
                    </p>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
